import { Auth } from "@angular/fire/auth";
import { Timestamp } from "@angular/fire/firestore";
import { State, Action, StateContext, Selector, Store } from "@ngxs/store";
import { Client as ClientStateShape, RegistryPersonDraft } from "../interface/client";
import {
  SearchClients,
  CreateClient,
  LoadClientByDocumentId,
  UpdateClient,
  CreateBillAuto,
  LoadBillAutosByClient,
  UpdateBillAuto,
  CreateBill,
  UpdateBillNotes,
  LoadBillsByClientAndState,
  LoadBillsByClient,
  CreatePayment,
  LoadPaymentsByBill,
  SetViewMode,
  LoadBillsByCriteria,
  LoadClientBalanceByState,
  PayAllBillsForClient,
  ClearBills,
  ClearClientLookup,
  ClearBillAutos,
  LoadBillsBalanceByCriteria,
  AnnulBill,
} from "./actions/client.actions";
import { UserState } from "./user.state";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Injectable, inject } from "@angular/core";
import { ClientsService } from "../firestore/clients.service";
import { BillsService } from "../firestore/bills.service";
import { BillsAutoService } from "../firestore/bills-auto.service";
import { PaymentsService } from "../firestore/payments.service";
import {
  IdentityLookupResult,
  IdentityLookupService,
} from "../firestore/identity-lookup.service";
import { buildClientSnapshot } from "../firestore/legacy-mappers";
import { Client as FsClient, Bill } from "../firestore/types";
import { AppUserUi } from "../interface/user";

const PAGE_SIZE = 12;

@Injectable()
@State<ClientStateShape>({
  name: "client",
  defaults: {
    ViewMode: "Clients",
    loadMorePeople: false,
    loadMoreBills: false,
    clientProfile: null,
    registryDraft: null,
  },
})
export class ClientState {
  // ---- Selectors ----------------------------------------------------------
  @Selector() static People({ People }: ClientStateShape) { return People; }
  @Selector() static PaysBill({ PaysBill }: ClientStateShape) { return PaysBill; }
  @Selector() static Bills({ Bills }: ClientStateShape) { return Bills; }
  @Selector() static loadMoreBills({ loadMoreBills }: ClientStateShape) { return loadMoreBills; }
  @Selector() static loadMorePeople({ loadMorePeople }: ClientStateShape) { return loadMorePeople; }
  @Selector() static clientProfile({ clientProfile }: ClientStateShape) { return clientProfile; }
  @Selector() static registryDraft({ registryDraft }: ClientStateShape) { return registryDraft; }
  @Selector() static BillsAuto({ BillsAuto }: ClientStateShape) { return BillsAuto; }
  @Selector() static BillsByCriteria({ BillsByCriteria }: ClientStateShape) { return BillsByCriteria; }
  @Selector() static loadMoreBillsByCriteria({ loadMoreBillsByCriteria }: ClientStateShape) { return loadMoreBillsByCriteria; }
  @Selector() static ViewMode({ ViewMode }: ClientStateShape) { return ViewMode; }
  @Selector() static BalanceByPersonAndState({ BalanceByPersonAndState }: ClientStateShape) { return BalanceByPersonAndState; }
  @Selector() static BalanceByCriteriaAndState({ BalanceByCriteriaAndState }: ClientStateShape) { return BalanceByCriteriaAndState; }

  // ---- Deps ---------------------------------------------------------------
  private readonly auth: Auth = inject(Auth);
  private readonly clients = inject(ClientsService);
  private readonly bills = inject(BillsService);
  private readonly billsAuto = inject(BillsAutoService);
  private readonly payments = inject(PaymentsService);
  private readonly identity = inject(IdentityLookupService);

  /**
   * In-memory cache of the last full Client document fetched per documentId.
   * Lets us avoid repeating the indexed query against Firestore when the
   * user navigates away and back, opens an edit modal, etc. Invalidated on
   * any write (create/edit/pay) that mutates the same client.
   */
  private clientByDocId = new Map<string, FsClient>();

  constructor(
    private store: Store,
    private snackBar: MatSnackBar
  ) {}

  // ---- Helpers ------------------------------------------------------------

  private creator(): { uid: string; name: string } {
    const fb = this.store.selectSnapshot(UserState.firebaseUser);
    const me: AppUserUi | null = this.store.selectSnapshot(UserState.me);
    /**
     * Prefer Firebase Auth's uid — it is available immediately after sign-in.
     * NGXS `firebaseUser` is patched only when `users/{uid}` emits, so using
     * only the store caused `uid === ""` during a short window (e.g. typing
     * a cédula in the new-client modal right after entering the CMS).
     */
    const uid =
      this.auth.currentUser?.uid ?? fb?.UID ?? fb?.id ?? "";
    return {
      uid,
      name: me?.givenName || me?.email || "",
    };
  }

  /**
   * Most recently shown error key, used to dedupe rapid-fire failures.
   * When several concurrent actions fail with the same message (typical of
   * a permission / index error that affects many queries at once) we open
   * the snackbar only for the first one, so the user doesn't see a stack of
   * identical toasts.
   */
  private lastErrorKey: string | null = null;
  private lastErrorAt = 0;

  private notifyError(err: any) {
    const message = String(err?.message ?? err ?? "");
    const now = Date.now();
    if (message === this.lastErrorKey && now - this.lastErrorAt < 4000) {
      return;
    }
    this.lastErrorKey = message;
    this.lastErrorAt = now;
    this.snackBar.dismiss();
    this.snackBar.open(`Error: ${message}`, "OK", {
      duration: 4000,
      panelClass: ["custom-snackbar-error"],
    });
  }

  /**
   * Same as `notifyError` but also rethrows. Use this in action handlers
   * whose dispatchers (typically modals) need to know about failures so
   * they can reset their loading state and stay open instead of closing
   * silently. Background loaders that just hydrate state should keep using
   * the swallowing variant.
   */
  private notifyAndRethrow(err: any): never {
    this.notifyError(err);
    throw err;
  }

  private snack(message: string) {
    this.snackBar.open(message, "OK", {
      duration: 3000,
      panelClass: ["custom-snackbar"],
    });
  }

  /** Cache helper: associates a fresh Client doc with its document id. */
  private rememberClient(c: FsClient) {
    const docId = c.documentId;
    if (docId) this.clientByDocId.set(docId, c);
  }

  /** Clears caches that depend on a specific client's data. */
  private invalidateClient(c?: FsClient | null) {
    if (!c) return;
    const docId = c.documentId;
    if (docId) this.clientByDocId.delete(docId);
  }

  // ---- Clients (Firestore `clients`) ------------------------------------

  @Action(SearchClients)
  async searchClients(
    { patchState, getState }: StateContext<ClientStateShape>,
    { criteria, Category }: SearchClients
  ) {
    const { loadMorePeople, People } = getState();
    if (!loadMorePeople) {
      patchState({ clientProfile: null, registryDraft: null });
    }
    // Pagination cursor by VALUE (companyName) — no extra getDoc per page,
    // unlike the previous startAfterId approach.
    const startAfter =
      loadMorePeople && People?.length
        ? People[People.length - 1].companyName
        : undefined;

    try {
      const list = await this.clients.find({
        criteria,
        category: Category,
        limit: PAGE_SIZE,
        startAfter,
      });

      // Cache each page so subsequent doc-id lookups are free.
      list.forEach((c) => this.rememberClient(c));

      const newPeople = !loadMorePeople || !startAfter
        ? list
        : [...(People ?? []), ...list];

      patchState({
        People: newPeople,
        loadMorePeople: list.length === PAGE_SIZE,
      });
    } catch (err) {
      this.notifyError(err);
    }
  }

  @Action(LoadClientByDocumentId)
  async loadClientByDocumentId(
    { patchState }: StateContext<ClientStateShape>,
    { DocumentId }: LoadClientByDocumentId
  ) {
    try {
      const normalised = String(DocumentId ?? "").replace(/\D/g, "");
      const cached = normalised ? this.clientByDocId.get(normalised) : null;

      // 1) Try the in-memory cache first; otherwise hit Firestore. The
      //    Firestore call is wrapped in its own try/catch so a denied query
      //    or a missing index doesn't kill the whole flow — we still want to
      //    fall back to the public registries (Hacienda/UCA) so the modal
      //    can pre-populate the new-client form.
      let client: FsClient | null = cached ?? null;
      if (!client) {
        try {
          client = await this.clients.findByDocumentId(DocumentId);
        } catch (firestoreErr) {
          console.warn(
            "findByDocumentId failed, falling back to public registries",
            firestoreErr
          );
        }
      }

      if (client) {
        this.rememberClient(client);
        patchState({ clientProfile: client, registryDraft: null });
        return;
      }

      // 2) Not in our DB (or Firestore failed) — pull what we can from the
      //    public registries (Registro Civil + Hacienda) so the user can
      //    create the client without having to type everything again.
      const lookup = await this.identity.lookup(DocumentId);
      patchState({
        clientProfile: null,
        registryDraft: lookup.found
          ? this.lookupToRegistryDraft(DocumentId, lookup)
          : null,
      });
    } catch (err) {
      this.notifyError(err);
    }
  }

  /**
   * Converts the legacy person-modal form shape into the new
   * `ClientsService.upsert` input. Tolerates disabled fields (which are
   * absent from `form.value`) by falling back to the currently selected
   * client in the state.
   */
  private formToUpsertInput(data: any) {
    const cp = this.store.selectSnapshot(ClientState.clientProfile);
    const rd = this.store.selectSnapshot(ClientState.registryDraft);

    return {
      companyName: data?.CompanyName ?? cp?.companyName ?? "",
      agent: data?.Agent ?? cp?.agent ?? "",
      category: data?.Category ?? cp?.category ?? (rd?.category ?? ""),
      additionalInfo:
        data?.AditionalInfo ?? cp?.additionalInfo ?? "",
      name: data?.Name_Person ?? (cp?.name ?? (rd?.name ?? "")),
      lastname: data?.Lastname_Person ?? (cp?.lastname ?? (rd?.lastname ?? "")),
      documentId:
        data?.DocumentId_Person ?? cp?.documentId ?? rd?.documentIdDigits ?? "",
      birthdate: data?.BirthDate_Person
        ? new Date(data.BirthDate_Person)
        : cp?.birthdate
          ? (cp.birthdate as any)?.toDate?.() ?? new Date(cp.birthdate as any)
          : rd?.birthdate
            ? rd.birthdate
            : null,
      personType: (data?.FK_PersonType ?? cp?.personType ?? rd?.personType)
        ? Number(data?.FK_PersonType ?? cp?.personType ?? rd?.personType)
        : null,
      description_address: data?.Description_Address ?? cp?.address ?? "",
      phoneNumber: data?.PhoneNumber ?? cp?.phoneNumber ?? "",
      email: data?.Email ?? cp?.email ?? "",
    };
  }

  private lookupToRegistryDraft(
    documentId: string,
    l: IdentityLookupResult
  ): RegistryPersonDraft {
    return {
      documentIdDigits: String(documentId).replace(/\D/g, ""),
      name: l.name ?? "",
      lastname: l.lastname ?? "",
      birthdate: l.birthdate ? new Date(l.birthdate) : null,
      personType: l.personType ?? 1,
      category: l.category ?? null,
      source: l.source,
    };
  }

  @Action(CreateClient)
  async createClient(
    { patchState, getState }: StateContext<ClientStateShape>,
    { data }: CreateClient
  ) {
    try {
      const created = await this.clients.upsert(
        this.formToUpsertInput(data),
        this.creator()
      );

      this.rememberClient(created);

      const { People } = getState();
      const found = People?.find((p) => p.id === created.id);
      patchState({
        People: found
          ? People.map((p) => (p.id === created.id ? created : p))
          : [created, ...(People ?? [])],
        clientProfile: created,
        registryDraft: null,
      });
    } catch (err) {
      this.notifyError(err);
    }
  }

  @Action(UpdateClient)
  async updateClient(
    { patchState, getState }: StateContext<ClientStateShape>,
    { data }: UpdateClient
  ) {
    try {
      const updated = await this.clients.upsert(
        { ...this.formToUpsertInput(data), id: (data as any).id },
        this.creator()
      );

      this.rememberClient(updated);

      const { People } = getState();
      patchState({
        People: People?.length
          ? People.map((p) => (p.id === updated.id ? updated : p))
          : [updated],
        clientProfile: updated,
        registryDraft: null,
      });
    } catch (err) {
      this.notifyError(err);
    }
  }

  // ---- Bills (Firestore `bills` / `billsAuto` / `payments`) ---------------

  @Action(LoadBillsByClientAndState)
  async getBillsByPersonAndState(
    { patchState, getState }: StateContext<ClientStateShape>,
    { personId, state, isNew }: LoadBillsByClientAndState
  ) {
    const { loadMoreBills, Bills } = getState();
    const startAfter =
      !loadMoreBills || isNew || !Bills?.length
        ? undefined
        : Bills[Bills.length - 1].createdAt;
    try {
      const list = await this.bills.findBills({
        clientId: personId,
        state,
        limit: PAGE_SIZE,
        startAfter,
      });
      const newBills = (!loadMoreBills && !startAfter) || isNew ? list : [...(Bills ?? []), ...list];
      patchState({
        Bills: newBills,
        loadMoreBills: list.length === PAGE_SIZE,
      });
    } catch (err) {
      this.notifyError(err);
    }
  }

  @Action(LoadBillsByClient)
  async getBillsByPerson(
    { patchState, getState }: StateContext<ClientStateShape>,
    { personId, state, start, end, isNew }: LoadBillsByClient
  ) {
    const { loadMoreBills, Bills } = getState();
    const startAfter =
      !loadMoreBills || isNew || !Bills?.length
        ? undefined
        : Bills[Bills.length - 1].createdAt;
    try {
      const list = await this.bills.findBills({
        clientId: personId,
        state,
        start,
        end,
        limit: PAGE_SIZE,
        startAfter,
      });
      const newBills = (!loadMoreBills && !startAfter) || isNew ? list : [...(Bills ?? []), ...list];
      patchState({
        Bills: newBills,
        loadMoreBills: list.length === PAGE_SIZE,
      });
    } catch (err) {
      this.notifyError(err);
    }
  }

  @Action(LoadBillsByCriteria)
  async getBillsByCriteriaAndState(
    { patchState, getState }: StateContext<ClientStateShape>,
    { criteria, state, start, end, category, isNew }: LoadBillsByCriteria
  ) {
    const { loadMoreBillsByCriteria, BillsByCriteria } = getState();
    const startAfter =
      !loadMoreBillsByCriteria || isNew || !BillsByCriteria?.length
        ? undefined
        : BillsByCriteria[BillsByCriteria.length - 1].createdAt;
    try {
      const list = await this.bills.findBills({
        state,
        start,
        end,
        category,
        limit: PAGE_SIZE,
        startAfter,
      });
      // Local filtering by criteria (substring on company name) — Firestore
      // can't combine a prefix range on companyNameLower with an unrelated
      // range on createdAt in the same query. We compensate by stopping
      // pagination if a page of results contains zero matches, so the
      // infinite-scroll doesn't drain reads forever.
      const filtered = criteria
        ? list.filter((b) =>
            b.client?.companyNameLower?.includes(criteria.trim().toLowerCase())
          )
        : list;
      const newBills = (!loadMoreBillsByCriteria && !startAfter) || isNew
        ? filtered
        : [...(BillsByCriteria ?? []), ...filtered];

      const morePagesAvailable = list.length === PAGE_SIZE;
      const filterDrainedPage =
        !!criteria && morePagesAvailable && filtered.length === 0;

      patchState({
        BillsByCriteria: newBills,
        // Only ask for more if the server says there are more AND the
        // current criteria didn't filter the entire page out.
        loadMoreBillsByCriteria: morePagesAvailable && !filterDrainedPage,
      });
    } catch (err) {
      this.notifyError(err);
    }
  }

  @Action(LoadClientBalanceByState)
  async getBalanceByPersonAndState(
    { patchState, getState: _getState }: StateContext<ClientStateShape>,
    { personId, state }: LoadClientBalanceByState
  ) {
    try {
      // Fast path: for "Pendientes" we already have the totalBalance
      // denormalized on the client doc. Use it instead of issuing a sum
      // aggregation against bills.
      if (Number(state) === 0) {
        const cachedClient = [...this.clientByDocId.values()].find(
          (c) => c.id === personId
        );
        if (cachedClient) {
          patchState({ BalanceByPersonAndState: Number(cachedClient.totalBalance ?? 0) });
          return;
        }
        // No cache yet — fall back to the aggregation query (still O(1) reads).
      }
      const sum = await this.bills.sumBalance({
        clientId: personId,
        state,
      });
      patchState({ BalanceByPersonAndState: sum || 0 });
    } catch (err) {
      this.notifyError(err);
    }
  }

  @Action(LoadBillsBalanceByCriteria)
  async getBalanceByCriteriaAndState(
    { patchState }: StateContext<ClientStateShape>,
    { criteria, state, start, end, category }: LoadBillsBalanceByCriteria
  ) {
    try {
      // When the user typed a criterion, Firestore aggregations can't filter
      // by `client.companyNameLower` prefix and `createdAt` range together,
      // so we fall back to listing + filtering for that case. Without a
      // criterion we use a single aggregation query — 1 billable read.
      if (criteria && criteria.trim().length > 0) {
        // Firestore can't combine prefix search on `client.companyNameLower`
        // with the other range filters, so we have to list + filter in
        // memory. To cap blast radius we hard-limit the read: in practice a
        // typed criterion narrows results enough that this is rarely hit,
        // but we surface a warning if it is so we know to paginate.
        const SEARCH_BALANCE_CAP = 500;
        const list = await this.bills.findBills({
          state,
          start,
          end,
          category,
          limit: SEARCH_BALANCE_CAP,
        });
        if (list.length === SEARCH_BALANCE_CAP) {
          console.warn(
            `Balance por criterio truncado a ${SEARCH_BALANCE_CAP} facturas; afina el rango de fechas o el filtro.`
          );
        }
        const lower = criteria.trim().toLowerCase();
        const filtered = list.filter((b) =>
          b.client?.companyNameLower?.includes(lower)
        );
        const sum = filtered.reduce(
          (acc, b) => acc + Number(b.balance || 0),
          0
        );
        patchState({ BalanceByCriteriaAndState: sum });
        return;
      }

      const sum = await this.bills.sumBalance({
        state,
        start,
        end,
        category,
      });
      patchState({ BalanceByCriteriaAndState: sum });
    } catch (err) {
      this.notifyError(err);
    }
  }

  @Action(CreateBill)
  async createBill(
    { patchState, getState }: StateContext<ClientStateShape>,
    { data }: CreateBill
  ) {
    try {
      const clientId = (data as any).FK_ContadoresRV_Person;
      const cachedClient = [...this.clientByDocId.values()].find(
        (c) => c.id === clientId
      );
      const created = await this.bills.createBill(
        {
          clientId,
          mount: Number((data as any).Mount),
          balance: Number((data as any).Balance ?? (data as any).Mount),
          notes: (data as any).Notes,
          // Hand the snapshot down so bills.service doesn't need to re-read
          // the client just to build the denormalized snapshot.
          clientSnapshot: cachedClient ? buildClientSnapshot(cachedClient) : undefined,
        },
        this.creator()
      );

      // Bump the cached totals so subsequent balance lookups stay accurate
      // without a re-fetch.
      if (cachedClient) {
        cachedClient.totalBalance = Number(cachedClient.totalBalance ?? 0) + Number(created.balance);
        cachedClient.unpaidCount = Number(cachedClient.unpaidCount ?? 0) + 1;
      }

      const { Bills, BalanceByPersonAndState } = getState();
      patchState({
        Bills: [created, ...(Bills ?? [])],
        BalanceByPersonAndState: Number(BalanceByPersonAndState ?? 0) + Number(created.balance),
      });
    } catch (err) {
      this.notifyAndRethrow(err);
    }
  }

  @Action(UpdateBillNotes)
  async editNotes(
    { patchState, getState }: StateContext<ClientStateShape>,
    { data }: UpdateBillNotes
  ) {
    try {
      const id = (data as any).id;
      const notes = (data as any).Notes ?? "";
      await this.bills.editNotes(id, notes);
      const { Bills, BillsByCriteria } = getState();
      const patchBill = (b: Bill) =>
        b.id === id
          ? { ...b, notes, updatedAt: Timestamp.now() }
          : b;
      patchState({
        Bills: Bills?.map(patchBill) ?? [],
        BillsByCriteria: BillsByCriteria?.map(patchBill) ?? [],
      });
    } catch (err) {
      this.notifyAndRethrow(err);
    }
  }

  @Action(AnnulBill)
  async annul(
    { patchState, getState }: StateContext<ClientStateShape>,
    { id }: AnnulBill
  ) {
    try {
      const { Bills, BalanceByPersonAndState } = getState();
      const target = Bills?.find((b) => b.id === id);
      await this.bills.annul(id);

      // Keep cached client totals in sync with the denormalized fields.
      if (target) {
        const cached = [...this.clientByDocId.values()].find(
          (c) => c.id === target.clientId
        );
        if (cached) {
          cached.totalBalance = Math.max(0, Number(cached.totalBalance ?? 0) - Number(target.balance ?? 0));
          if (Number(target.paid) === 0) {
            cached.unpaidCount = Math.max(0, Number(cached.unpaidCount ?? 0) - 1);
          }
        }
      }

      patchState({
        Bills:
          Bills?.map((b) =>
            b.id === id
              ? { ...b, paid: 2, balance: 0, updatedAt: Timestamp.now() }
              : b
          ) ?? [],
        BalanceByPersonAndState: target
          ? Math.max(0, Number(BalanceByPersonAndState ?? 0) - Number(target.balance))
          : BalanceByPersonAndState,
      });
    } catch (err) {
      this.notifyAndRethrow(err);
    }
  }

  // ---- BillsAuto ----------------------------------------------------------

  @Action(LoadBillAutosByClient)
  async billsAutoByPerson(
    { patchState }: StateContext<ClientStateShape>,
    { personId }: LoadBillAutosByClient
  ) {
    try {
      const list = await this.billsAuto.findByClient(personId);
      patchState({ BillsAuto: list });
    } catch (err) {
      this.notifyError(err);
    }
  }

  @Action(CreateBillAuto)
  async createBillAuto(
    { patchState, getState }: StateContext<ClientStateShape>,
    { data }: CreateBillAuto
  ) {
    try {
      const clientId = (data as any).FK_ContadoresRV_Person;
      const cached = [...this.clientByDocId.values()].find(
        (c) => c.id === clientId
      );
      const created = await this.billsAuto.create(
        {
          clientId,
          mount: Number((data as any).Mount),
          notes: (data as any).Notes,
          isActive: !!(data as any).IsActive,
          generationDay: Number((data as any).GenerationDay ?? 1),
          clientSnapshot: cached ? buildClientSnapshot(cached) : undefined,
        },
        this.creator()
      );
      const { BillsAuto } = getState();
      patchState({ BillsAuto: [created, ...(BillsAuto ?? [])] });
    } catch (err) {
      this.notifyAndRethrow(err);
    }
  }

  @Action(UpdateBillAuto)
  async editBillAuto(
    { patchState, getState }: StateContext<ClientStateShape>,
    { data }: UpdateBillAuto
  ) {
    try {
      const id = (data as any).id;
      const patch = {
        mount: (data as any).Mount !== undefined ? Number((data as any).Mount) : undefined,
        notes: (data as any).Notes,
        isActive:
          (data as any).IsActive !== undefined
            ? !!(data as any).IsActive
            : undefined,
        generationDay:
          (data as any).GenerationDay !== undefined
            ? Number((data as any).GenerationDay)
            : undefined,
      };
      await this.billsAuto.update(id, patch);
      const { BillsAuto } = getState();
      patchState({
        BillsAuto:
          BillsAuto?.map((b) =>
            b.id === id
              ? {
                  ...b,
                  ...(patch.mount !== undefined ? { mount: patch.mount } : {}),
                  ...(patch.notes !== undefined ? { notes: patch.notes } : {}),
                  ...(patch.isActive !== undefined
                    ? { isActive: patch.isActive }
                    : {}),
                  ...(patch.generationDay !== undefined
                    ? { generationDay: patch.generationDay }
                    : {}),
                  updatedAt: Timestamp.now(),
                }
              : b
          ) ?? [],
      });
    } catch (err) {
      this.notifyAndRethrow(err);
    }
  }

  // ---- Payments -----------------------------------------------------------

  @Action(LoadPaymentsByBill)
  async paysByBill(
    { patchState }: StateContext<ClientStateShape>,
    { billId }: LoadPaymentsByBill
  ) {
    try {
      const list = await this.payments.findByBill(billId);
      patchState({ PaysBill: list });
    } catch (err) {
      this.notifyError(err);
    }
  }

  @Action(CreatePayment)
  async createPay(
    { patchState, getState }: StateContext<ClientStateShape>,
    { data }: CreatePayment
  ) {
    try {
      await this.payments.pay(
        {
          billId: (data as any).FK_ContadoresRV_Bill,
          mount: Number((data as any).Mount),
          notes: (data as any).Notes,
          electronicBill: (data as any).ElectronicBill,
          fileUrl: (data as any).FileUrl || null,
          fileName: (data as any).FileName || null,
        },
        this.creator()
      );

      const { Bills, BillsByCriteria, BalanceByPersonAndState } = getState();
      const id = (data as any).FK_ContadoresRV_Bill;
      const mount = Number((data as any).Mount);
      const updateBill = (b: Bill) => {
        if (String(b.id) !== String(id)) return b;
        const newBalance = Math.max(0, Number(b.balance) - mount);
        return {
          ...b,
          balance: newBalance,
          paid: newBalance <= 0 ? 1 : b.paid,
          updatedAt: Timestamp.now(),
        };
      };

      // Keep client cache in sync with the new totals.
      const target = Bills?.find((b) => b.id === id);
      if (target) {
        const cached = [...this.clientByDocId.values()].find(
          (c) => c.id === target.clientId
        );
        if (cached) {
          cached.totalBalance = Math.max(0, Number(cached.totalBalance ?? 0) - mount);
          const newBalance = Math.max(0, Number(target.balance) - mount);
          if (newBalance <= 0 && Number(target.paid) === 0) {
            cached.unpaidCount = Math.max(0, Number(cached.unpaidCount ?? 0) - 1);
          }
        }
      }

      patchState({
        Bills: Bills?.map(updateBill) ?? [],
        BillsByCriteria: BillsByCriteria?.map(updateBill) ?? [],
        BalanceByPersonAndState: Math.max(0, Number(BalanceByPersonAndState ?? 0) - mount),
      });
    } catch (err) {
      this.notifyAndRethrow(err);
    }
  }

  @Action(PayAllBillsForClient)
  async payAll(
    { patchState, getState }: StateContext<ClientStateShape>,
    { data }: PayAllBillsForClient
  ) {
    try {
      const clientId = (data as any).FK_ContadoresRV_Person;
      const result = await this.payments.payAllForClient(
        {
          clientId,
          electronicBill: (data as any).ElectronicBill,
          notes: (data as any).Notes,
          fileUrl: (data as any).FileUrl || null,
          fileName: (data as any).FileName || null,
        },
        this.creator()
      );

      if (result.count > 0) {
        const cached = [...this.clientByDocId.values()].find(
          (c) => c.id === clientId
        );
        if (cached) {
          cached.totalBalance = 0;
          cached.unpaidCount = 0;
        }

        const { Bills } = getState();
        patchState({
          Bills: Bills?.map((b) =>
            Number(b.paid) === 0
              ? { ...b, paid: 1, balance: 0, updatedAt: Timestamp.now() }
              : b
          ) ?? [],
          BalanceByPersonAndState: 0,
        });
        this.snack(
          `Pagadas ${result.count} facturas, total ₡${result.total.toLocaleString()}`
        );
      } else {
        this.snack("Este cliente no tiene facturas pendientes");
      }
    } catch (err) {
      this.notifyAndRethrow(err);
    }
  }

  // ---- Misc ---------------------------------------------------------------

  @Action(ClearBillAutos)
  cleanBillsAuto({ patchState }: StateContext<ClientStateShape>) {
    patchState({ BillsAuto: [] });
  }

  @Action(ClearClientLookup)
  clearClientLookup({ patchState }: StateContext<ClientStateShape>) {
    patchState({ clientProfile: null, registryDraft: null });
  }

  @Action(ClearBills)
  cleanBills({ patchState }: StateContext<ClientStateShape>) {
    patchState({ Bills: [], BalanceByPersonAndState: null });
  }

  @Action(SetViewMode)
  setViewMode(
    { patchState }: StateContext<ClientStateShape>,
    { ViewMode }: SetViewMode
  ) {
    const leaveClients =
      ViewMode !== "Clients"
        ? { clientProfile: null as null, registryDraft: null }
        : {};
    patchState({ ViewMode, ...leaveClients });
  }
}
